import React, { useState } from 'react';
import SbEditable from 'storyblok-react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Sentry from '@sentry/react';
import InputMask from 'react-input-mask';
import * as Yup from 'yup';

import { leads, metrics } from '../../services';
import TrackFormDirty from '../TrackFormDirty';
import { PhoneRegex } from '../../constants';
import './lead-energyshield.scss';

// I have no idea what this is doing. Using https://github.com/formium/formik/blob/master/examples/MultistepWizard.js

const defaultFormValues = {
  name: '',
  email: '',
  phone: '',
  zip: '',
  homeSize: '',
  existingSolar: '',
  isHomeowner: '',
};

const Wizard = ({ children, initialValues, onSubmit, accentColor }) => {
  const [isDirty, setIsDirty] = useState(false);
  const [stepNumber, setStepNumber] = useState(0);
  const steps = React.Children.toArray(children);
  const [snapshot, setSnapshot] = useState(initialValues);

  const step = steps[stepNumber];
  const totalSteps = steps.length;
  const isLastStep = stepNumber === totalSteps - 1;
  const buttonClasses = [`accent-${accentColor}`];

  const next = (values) => {
    setSnapshot(values);
    setStepNumber(Math.min(stepNumber + 1, totalSteps - 1));
  };

  const handleSubmit = async (values, bag) => {
    if (step.props.onSubmit) {
      await step.props.onSubmit(values, bag);
    }

    if (isLastStep) {
      return onSubmit(values, bag);
    } else {
      bag.setTouched({});
      next(values);
    }
  };

  return (
    <Formik
      initialValues={snapshot}
      onSubmit={handleSubmit}
      validationSchema={step.props.validationSchema}
    >
      {(formik) => (
        <Form>
          <TrackFormDirty
            formComponent="LeadEnergyShield"
            isDirty={isDirty}
            setIsDirty={setIsDirty}
          />
          {step}
          <button disabled={formik.isSubmitting} className={buttonClasses.join(' ')} type="submit">
            {isLastStep
              ? formik.isSubmitting
                ? 'Submitting...'
                : 'Request My Results'
              : 'Continue'}
          </button>
          {isLastStep && (
            <span className="disclaimer">
              By submitting this form, I agree to the {' '}
                      <a href="/privacy-policy">Privacy Policy</a> and consent to be contacted by or
                      on behalf of Swell Energy, including by email, calls, and text messages to any
                      telephone number that I provide, about my interest in Swell Energy products
                      and services. I understand my consent is not required to purchase.
            </span>
          )}
        </Form>
      )}
    </Formik>
  );
};

const WizardStep = ({ children }) => children;

const LeadEnergyShield = (props) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [submitState, setSubmitState] = useState('');

  const onSubmit = async (values) => {
    setSubmitState('');

    if (!values.email && !values.phone) {
      setSubmitState('failed');
      setErrorMessage('Please provide either your email address or phone number (or both).');
      return;
    }

    try {
      const utmContent = props.blok.integration_content || 'Swell';
      const isProd =
        window.location.origin.indexOf('swellenergy.com') > -1 &&
        window.location.origin.indexOf('sbx') === -1;
      const leadSourceAccounts = { dev: '0016100000fehkAAAQ', prod: '0014M00001k8A7fQAE' };

      await leads.submitLead({
        ...values,
        utmContent,
        leadSourceMedium: 'Swell Energy',
        leadSource: 'Online Media',
        leadSourceAccount: isProd ? leadSourceAccounts.prod : leadSourceAccounts.dev,
      });

      setSubmitState('success');
      metrics.trackLeadFormSubmit(
        window.location.origin + window.location.pathname,
        'LeadFormEnergyShield',
        utmContent
      );
    } catch (e) {
      Sentry.captureException(e, {
        lead: values,
      });
      setErrorMessage('We had a problem processing your request. Please try again.');
      setSubmitState('failed');
    }
  };

  const successMessage =
    props.blok.success_message ||
    'Thank you! You are one step closer to having affordable and reliable energy. A Swell Energy representative will contact you shortly to discuss your renewable energy options.';
  const formClasses = ['fields'];
  formClasses.push('orientation-' + props.blok.orientation);
  formClasses.push('theme-' + props.blok.theme);

  const errorClasses = ['error'];
  errorClasses.push('theme-' + props.blok.theme);

  return (
    <SbEditable content={props.blok}>
      <div className="energyshield">
        {submitState === 'failed' && (
          <div className={formClasses.join(' ')}>
            <span className={errorClasses.join(' ')}>{errorMessage}</span>
          </div>
        )}
        {submitState !== 'success' && (
          <Wizard
            initialValues={defaultFormValues}
            onSubmit={onSubmit}
            accentColor={props.blok.accent_color}
          >
            <WizardStep
              validationSchema={Yup.object().shape({
                homeSize: Yup.string().required('Required'),
                existingSolar: Yup.string().required('Required'),
                isHomeowner: Yup.string().required('Required'),
              })}
            >
              <div className={formClasses.join(' ')}>
                <div className="field">
                  <label htmlFor="homeSize">How big is your home?</label>
                  <Field name="homeSize" as="select">
                    <option value="">Please select</option>
                    <option value="Under 1,000 sf">Under 1,000 sf</option>
                    <option value="1,000-3,000 sf">1,000-3,000 sf</option>
                    <option value="Over 3,000 sf">Over 3,000 sf</option>
                  </Field>
                  <ErrorMessage className="error" component="div" name="homeSize" />
                </div>
                <div className="field">
                  <label htmlFor="existingSolar">Do you have solar panels?</label>
                  <Field name="existingSolar" as="select">
                    <option value="">Please select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Field>
                  <ErrorMessage className="error" component="div" name="existingSolar" />
                </div>
                <div className="field">
                  <label htmlFor="isHomeowner">Are you a homeowner?</label>
                  <Field name="isHomeowner" as="select">
                    <option value="">Please select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Field>
                  <ErrorMessage className="error" component="div" name="isHomeowner" />
                </div>
              </div>
            </WizardStep>
            <WizardStep
              validationSchema={Yup.object().shape({
                name: Yup.string().required('Required'),
                email: Yup.string().email('Enter a valid e-mail address'),
                phone: Yup.string().matches(PhoneRegex, 'Enter your phone number'),
                zip: Yup.string().required('Required').matches(/\d{5}/, 'Enter your ZIP code'),
              })}
            >
              <div className={formClasses.join(' ')}>
                <div className="field">
                  <label htmlFor="name">Name</label>
                  <Field name="name" placeholder="Name" />
                  <ErrorMessage className="error" component="div" name="name" />
                </div>
                <div className="field">
                  <label htmlFor="email">Email</label>
                  <Field name="email" placeholder="Email" />
                  <ErrorMessage className="error" component="div" name="email" />
                </div>
                <div className="field">
                  <label htmlFor="phone">Phone</label>
                  <Field name="phone" placeholder="Phone">
                    {({ field }) => (
                      <InputMask
                        {...field}
                        mask="(999) 999-9999"
                        placeholder="Phone"
                        type="text"
                        alwaysShowMask={false}
                      />
                    )}
                  </Field>
                  <ErrorMessage className="error" component="div" name="phone" />
                </div>
                <div className="field">
                  <label htmlFor="zip">ZIP Code</label>
                  <Field name="zip" placeholder="ZIP Code">
                    {({ field }) => (
                      <InputMask
                        {...field}
                        mask="99999"
                        type="text"
                        placeholder="ZIP Code"
                        alwaysShowMask={false}
                      />
                    )}
                  </Field>
                  <ErrorMessage className="error" component="div" name="zip" />
                </div>
              </div>
            </WizardStep>
          </Wizard>
        )}
        {submitState === 'success' && (
          <div className={formClasses.join(' ')}>
            <span className="success">{successMessage}</span>
          </div>
        )}
      </div>
    </SbEditable>
  );
};

export default LeadEnergyShield;
